<!-- src/components/common/DaumPostcodeModal.vue -->
<template>
  <!-- Bootstrap Modal -->
  <div
    class="modal fade daum-postcode-modal"
    tabindex="-1"
    :id="modalId"
    ref="modalEl"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="labelId"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-fullscreen-sm-down"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" :id="labelId">{{ resolvedTitle }}</h5>
          <button
            type="button"
            class="btn-close"
            ref="closeBtnEl"
            @click="close"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body p-0">
          <!-- 로딩 스피너 -->
          <div
            v-if="loading"
            class="d-flex align-items-center justify-content-center py-5"
            aria-live="polite"
          >
            <div class="spinner-border" role="status" aria-hidden="true"></div>
            <span class="ms-2">불러오는 중…</span>
          </div>

          <!-- 카카오 우편번호 임베드 컨테이너 -->
          <div
            v-show="!loading"
            ref="embedTarget"
            class="postcode-embed"
            role="region"
            aria-label="우편번호 검색 프레임"
          ></div>
        </div>

        <div class="modal-footer">
          <div class="text-muted small me-auto">
            * 주소 선택 시 자동으로 닫히도록 설정됨 (옵션 변경 가능)
          </div>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="close"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  computed,
  nextTick,
} from "vue";
import { useI18n } from "vue-i18n";
import {
  ensureDaumPostcode,
  isDaumPostcodeLoaded,
} from "@/composables/useDaumPostcode";

/* ========================
 * Props / Emits
 * ===================== */
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: "" },
  titleKey: { type: String, default: "" },
  initialQuery: { type: String, default: "" },
  autoCloseOnComplete: { type: Boolean, default: true },
  initialHeight: { type: Number, default: 420 }, // 최초 표시 높이(이후 onresize가 관리)
  theme: { type: Object, default: null },
  restoreFocus: { type: Boolean, default: true },
});
const emit = defineEmits([
  "update:modelValue",
  "opened",
  "closed",
  "complete",
  "error",
]);

/* ========================
 * i18n Title
 * ===================== */
const { t } = useI18n();
const resolvedTitle = computed(() => {
  if (props.titleKey) return t(props.titleKey);
  if (props.title) return props.title;
  const fallback = t("common.addressSearch", "우편번호 검색");
  return typeof fallback === "string" ? fallback : "우편번호 검색";
});

/* ========================
 * Refs / State
 * ===================== */
const modalEl = ref(null);
const closeBtnEl = ref(null);
const embedTarget = ref(null);
const loading = ref(false);
let bsModal = null; // Bootstrap Modal instance
let postcode = null; // daum.Postcode instance
let shownOnce = false; // 첫 표시 이후 재임베드 방지
let usingFallback = false;
let lastActiveEl = null;

/* ========================
 * IDs
 * ===================== */
const modalId = `postcode-modal-${Math.random().toString(36).slice(2)}`;
const labelId = `${modalId}-label`;

/* ========================
 * Helpers: Focus Trap (Fallback only)
 * ===================== */
function isVisible() {
  const el = modalEl.value;
  return (
    !!el && (el.classList.contains("show") || el.classList.contains("d-block"))
  );
}
function getFocusable() {
  const root = modalEl.value;
  if (!root) return [];
  const sel =
    'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const nodes = Array.from(root.querySelectorAll(sel));
  return nodes.filter(
    (n) => n.offsetParent !== null || n === document.activeElement
  );
}
function keydownHandler(e) {
  if (!usingFallback || !isVisible()) return;
  if (e.key === "Escape") {
    e.preventDefault();
    close();
    return;
  }
  if (e.key !== "Tab") return;

  const f = getFocusable();
  if (!f.length) {
    e.preventDefault();
    return;
  }
  const first = f[0];
  const last = f[f.length - 1];
  const current = document.activeElement;

  if (e.shiftKey) {
    if (current === first || !f.includes(current)) {
      last.focus();
      e.preventDefault();
    }
  } else {
    if (current === last || !f.includes(current)) {
      first.focus();
      e.preventDefault();
    }
  }
}
function addFallbackFocusTrap() {
  document.addEventListener("keydown", keydownHandler, true);
}
function removeFallbackFocusTrap() {
  document.removeEventListener("keydown", keydownHandler, true);
}

/* ========================
 * HEIGHT CONTROL (safe, no recursion)
 * ===================== */
function toPx(v) {
  if (!v) return 0;
  const r =
    parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const s = String(v).trim();
  if (s.endsWith("rem")) return parseFloat(s) * r;
  if (s.endsWith("px")) return parseFloat(s);
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}
function computeMaxEmbedHeight() {
  const vp = window.visualViewport?.height || window.innerHeight || 720;
  const headerH =
    modalEl.value?.querySelector(".modal-header")?.offsetHeight || 0;
  const footerH =
    modalEl.value?.querySelector(".modal-footer")?.offsetHeight || 0;
  const contentEl = modalEl.value?.querySelector(".modal-content");
  const csContent = contentEl ? getComputedStyle(contentEl) : null;
  const borderT = csContent ? parseFloat(csContent.borderTopWidth) || 0 : 0;
  const borderB = csContent ? parseFloat(csContent.borderBottomWidth) || 0 : 0;
  const csModal = modalEl.value ? getComputedStyle(modalEl.value) : null;
  const modalMarginRaw = csModal
    ? csModal.getPropertyValue("--bs-modal-margin")
    : "";
  const modalMarginY = modalMarginRaw ? toPx(modalMarginRaw) * 2 : 0;
  return Math.max(
    320,
    Math.floor(vp - modalMarginY - headerH - footerH - borderT - borderB)
  );
}
let lastWidgetHeight = props.initialHeight; // 위젯이 요구한 높이(최근값)
let lastAppliedHeight = 0; // 실제 적용된 높이
let internalUpdate = false; // 내가 바꾸는 중이면 onresize 무시

function getDaumLayerEl() {
  const root = embedTarget.value;
  if (!root) return null;
  return (
    root.querySelector('div[id*="layer"]') || root.firstElementChild || null
  );
}
function applyEmbedHeightBounded(nextHeight) {
  // 외부(카카오/윈도)에서 전달된 높이 갱신
  if (typeof nextHeight === "number" && nextHeight > 0)
    lastWidgetHeight = nextHeight;

  const cap = computeMaxEmbedHeight();
  const h = Math.min(Math.max(320, Math.floor(lastWidgetHeight)), cap);

  // 재진입/중복 방지
  if (internalUpdate) return;
  if (Math.abs(h - lastAppliedHeight) < 1) return;

  internalUpdate = true;
  if (embedTarget.value) embedTarget.value.style.height = `${h}px`;
  const layer = getDaumLayerEl();
  if (layer) layer.style.height = `${h}px`;
  lastAppliedHeight = h;

  // 다음 프레임에 해제: onresize 루프 차단
  requestAnimationFrame(() => {
    internalUpdate = false;
  });
}

/* ========================
 * Show / Hide
 * ===================== */
function show() {
  lastActiveEl = document.activeElement;

  if (!bsModal) {
    const Modal = window.bootstrap && window.bootstrap.Modal;
    if (!Modal) {
      // Fallback: Bootstrap 미탑재
      usingFallback = true;
      modalEl.value?.classList.add("show", "d-block");
      document.body.classList.add("modal-open");
      addFallbackFocusTrap();
      mountPostcode()
        .then(async () => {
          await nextTick();
          closeBtnEl.value && closeBtnEl.value.focus();
          emit("opened");
        })
        .catch((err) => emit("error", err));
      return;
    }
    usingFallback = false;
    bsModal = new Modal(modalEl.value, {
      backdrop: "static",
      keyboard: true,
      focus: true,
    });
    modalEl.value.addEventListener("shown.bs.modal", onShown, { once: false });
    modalEl.value.addEventListener("hidden.bs.modal", onHidden, {
      once: false,
    });
  }
  bsModal.show();
}
function hide() {
  if (bsModal) {
    bsModal.hide();
  } else {
    // fallback
    modalEl.value?.classList.remove("show", "d-block");
    document.body.classList.remove("modal-open");
    removeFallbackFocusTrap();
    teardownPostcode();
    emit("closed");
    restoreFocusToTrigger();
  }
}
function close() {
  emit("update:modelValue", false);
  hide();
}

async function onShown() {
  try {
    await mountPostcode();
    // 표시 직후 보정
    requestAnimationFrame(() => applyEmbedHeightBounded());
    emit("opened");
  } catch (err) {
    emit("error", err);
  }
}
function onHidden() {
  teardownPostcode();
  emit("closed");
  restoreFocusToTrigger();
}

/* ========================
 * Postcode embed
 * ===================== */
async function mountPostcode() {
  if (shownOnce && postcode && embedTarget.value?.firstChild) return;
  loading.value = true;

  if (!isDaumPostcodeLoaded()) await ensureDaumPostcode();

  if (embedTarget.value) {
    embedTarget.value.innerHTML = "";
    lastWidgetHeight = props.initialHeight;
    applyEmbedHeightBounded(lastWidgetHeight); // 초기 높이(이후 onresize가 관리)
  }

  postcode = new window.daum.Postcode({
    theme: props.theme || undefined,
    oncomplete: (data) => {
      emit("complete", data);
      if (props.autoCloseOnComplete) close();
    },
    // 카카오 높이 전달 → 뷰포트 캡 + 루프 방지 적용
    onresize: (size) => applyEmbedHeightBounded(size?.height),
  });

  const embedOpts = { autoClose: false };
  if (props.initialQuery) embedOpts.q = props.initialQuery;

  postcode.embed(embedTarget.value, embedOpts);

  shownOnce = true;
  loading.value = false;

  // 모달 렌더 완료 후 한 번 더 보정
  requestAnimationFrame(() => applyEmbedHeightBounded());
}
function teardownPostcode() {
  if (embedTarget.value) embedTarget.value.innerHTML = "";
  postcode = null;
}

/* ========================
 * Focus restore
 * ===================== */
function restoreFocusToTrigger() {
  if (!props.restoreFocus) return;
  if (lastActiveEl && typeof lastActiveEl.focus === "function") {
    try {
      const isInDoc = document.contains(lastActiveEl);
      if (isInDoc) lastActiveEl.focus();
    } catch {
      /* ignore */
    }
  }
}

/* ========================
 * Lifecycle / Watch
 * ===================== */
const onWindowResize = () => applyEmbedHeightBounded();
const onOrientationChange = () => applyEmbedHeightBounded();

onMounted(() => {
  if (props.modelValue) show();
  window.addEventListener("resize", onWindowResize, { passive: true });
  window.addEventListener("orientationchange", onOrientationChange);
});
onBeforeUnmount(() => {
  try {
    modalEl.value?.removeEventListener("shown.bs.modal", onShown);
    modalEl.value?.removeEventListener("hidden.bs.modal", onHidden);
    if (bsModal) {
      try {
        bsModal.hide();
      } catch (err) {
        bsModal.dispose();
      }
    }
  } finally {
    removeFallbackFocusTrap();
    teardownPostcode();
  }
  window.removeEventListener("resize", onWindowResize);
  window.removeEventListener("orientationchange", onOrientationChange);
});

watch(
  () => props.modelValue,
  (v) => {
    if (v) show();
    else hide(); // 여기서는 close() 대신 hide()만 호출(불필요한 emit 방지)
  }
);
</script>

<style scoped>
/* 카카오 컨테이너: 폭 100%, 높이는 onresize/JS가 관리 */
.postcode-embed {
  width: 100%;
}
.postcode-embed :deep(iframe) {
  display: block;
  width: 100% !important;
  height: 100% !important;
  border: 0;
}

/* ---- Address Popup Standard ---- */
.daum-postcode-modal .modal-dialog {
  /* 데스크톱 폭 고정, 모바일은 modal-fullscreen-sm-down으로 풀스크린 */
  --bs-modal-width: 520px;
}

.daum-postcode-modal .modal-content {
  border-radius: 0.5rem;
  overflow: hidden;
  /* 모달 자체가 뷰포트를 넘지 않게 */
  max-height: calc(100vh - var(--bs-modal-margin) * 2);
}

/* 헤더·푸터 컴팩트 */
.daum-postcode-modal .modal-header,
.daum-postcode-modal .modal-footer {
  padding: 0.625rem 0.75rem; /* 10px 12px */
}
.daum-postcode-modal .modal-title {
  font-size: 1rem; /* 16px */
  line-height: 1.25rem;
  margin: 0;
}
.daum-postcode-modal .modal-body {
  padding: 0;
}

/* 다크 모드(선택) */
:root[data-bs-theme="dark"] .daum-postcode-modal .modal-content {
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
}
</style>
